/**
 * 🔍 DISCOVER TAB - Architecture Mozart/Jobs/Tesla
 * 
 * L'onglet de découverte et swipe des profils.
 * Le cœur de Heartbeats !
 */

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, X, Star, MapPin, Crown, Shield, Users, 
  MessageCircle, Sparkles, Gift, Zap, Eye, Video, Filter, Moon, PawPrint
} from 'lucide-react';
import { Button } from '../../ui/button';
import { useDiscover } from '../../../hooks/heartbeats';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';

export const DiscoverTab = ({ 
  customerId, 
  isDarkMode, 
  profile,
  onShowSetup,
  onOpenChat,
  onSendGift,
  onShowSuperLike
}) => {
  const discover = useDiscover(customerId);
  const { currentProfile, isLoading, swipeDirection, filterVideoOnly, toggleVideoFilter } = discover;

  useEffect(() => {
    if (customerId && profile) {
      discover.loadProfiles(true);
    }
  }, [customerId, profile]);

  // 🌙 Message limite quotidienne atteinte
  if (discover.remainingToday === 0 && !isLoading) {
    return (
      <div className="text-center py-12">
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="text-6xl mb-4"
        >
          🌙
        </motion.div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Tu as découvert 3 vraies personnes aujourd&apos;hui !
        </h3>
        <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Prends le temps de bien les connaître... Reviens demain pour de nouvelles rencontres 💜
        </p>
        <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          🎭 Ici, la qualité prime sur la quantité
        </p>
      </div>
    );
  }

  // Pas de profils
  if (!currentProfile && !isLoading) {
    return (
      <div className="text-center py-12">
        <Users className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
        {profile?.isVisible ? (
          <>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              🎉 T&apos;es le premier, champion !
            </h3>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              Ton profil est actif. Les autres vont bientôt débarquer !
            </p>
            <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Fais passer le mot au magasin, plus on est de fous, plus on match ! 🔥
            </p>
          </>
        ) : (
          <>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              🚀 Sois le premier à oser !
            </h3>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              L&apos;aventure commence avec toi ! Lance-toi 🔥
            </p>
            <Button
              onClick={onShowSetup}
              className="mt-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            >
              Créer mon profil
            </Button>
          </>
        )}
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin text-4xl mb-4">💜</div>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* 📅 Compteur de découvertes quotidiennes */}
      <div className="flex items-center justify-center mb-4">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
          isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
        }`}>
          <Moon className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {discover.remainingToday} rencontre{discover.remainingToday > 1 ? 's' : ''} restante{discover.remainingToday > 1 ? 's' : ''} aujourd&apos;hui
          </span>
          <div className="flex gap-1">
            {[...Array(discover.dailyLimit || 3)].map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full ${
                  i < (discover.remainingToday || 0)
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                    : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 📹 Barre de filtres */}
      <div className="flex items-center justify-between mb-4 px-2">
        <div className="text-sm text-gray-500">
          {filterVideoOnly ? '📹 Profils avec vidéo' : '🎭 Avatars uniquement'}
        </div>
        <button
          onClick={() => toggleVideoFilter()}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            filterVideoOnly
              ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
              : isDarkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <Video className="w-4 h-4" />
          {filterVideoOnly ? 'Vidéo ON' : 'Filtre vidéo'}
        </button>
      </div>

      {/* Carte profil - AVATAR UNIQUEMENT */}
      <motion.div
        key={currentProfile.customerId}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: 1,
          x: swipeDirection === 'left' ? -300 : swipeDirection === 'right' ? 300 : 0,
          rotate: swipeDirection === 'left' ? -15 : swipeDirection === 'right' ? 15 : 0
        }}
        transition={{ duration: 0.3 }}
        className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} shadow-lg`}
      >
        {/* Photo/Avatar - 🎭 AVATAR UNIQUEMENT (dignité humaine - pas de swipe sur visage) */}
        <div className="relative h-64 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 flex items-center justify-center overflow-hidden">
          {/* Toujours afficher l'AVATAR - jamais la photo au swipe */}
          {currentProfile.avatarImage ? (
            <img 
              src={currentProfile.avatarImage} 
              alt={currentProfile.displayName}
              className="w-56 h-56 object-cover rounded-full border-4 border-white/30 shadow-xl"
            />
          ) : (
            <div className="text-8xl">
              {currentProfile.avatar || '😊'}
            </div>
          )}
          
          {/* Message philosophique */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/40 backdrop-blur-sm text-white/80 px-3 py-1 rounded-full text-xs">
            🎭 Découvre la personne, pas juste son visage
          </div>
          
          {/* Badges */}
          {currentProfile.atStore && (
            <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1 animate-pulse">
              <MapPin className="w-4 h-4" />
              Au magasin !
            </div>
          )}
          {currentProfile.isRegular && (
            <div className="absolute top-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
              <Crown className="w-4 h-4" />
              Habitué
            </div>
          )}
          {currentProfile.badges && currentProfile.badges.length > 0 && (
            <div className="absolute bottom-2 left-2 flex gap-1">
              {currentProfile.badges.includes('verified') && (
                <div className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Vérifié
                </div>
              )}
              {currentProfile.badges.includes('email') && (
                <div className="bg-cyan-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  📧
                </div>
              )}
            </div>
          )}
          
          {/* 📹 Badge Vidéo */}
          {currentProfile.hasVideo && (
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg"
            >
              <Video className="w-3 h-3" />
              Vidéo dispo
            </motion.div>
          )}
          
          {/* 💜 Badge "Âme compatible" - Psyche matching */}
          {currentProfile.soulCompatibility?.show_soul_badge && (
            <motion.div 
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`absolute bottom-2 right-2 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg ${
                currentProfile.soulCompatibility.soul_badge_type === 'healing_match'
                  ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white animate-pulse'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
              }`}
            >
              <span className="text-base">🧠</span>
              <span>
                {currentProfile.soulCompatibility.soul_badge_type === 'healing_match' 
                  ? '💜 Âme guérisseuse' 
                  : '💜 Âme compatible'}
              </span>
            </motion.div>
          )}
          
          {/* 🌙 Badge Nyx 2.0 - Compatibilité d'ombre */}
          {currentProfile.nyxCompatibility?.hasNyx2 && currentProfile.nyxCompatibility?.badge && (
            <motion.div 
              initial={{ scale: 0, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="absolute bottom-12 right-2 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg bg-gradient-to-r from-indigo-900 to-purple-900 text-white border border-purple-500/50"
            >
              <Moon className="w-3 h-3" />
              <span>{currentProfile.nyxCompatibility.badge.emoji} {currentProfile.nyxCompatibility.badge.name}</span>
            </motion.div>
          )}
          
          {/* 🐾 Badge Animaux de compagnie */}
          {currentProfile.pets && currentProfile.pets.length > 0 && (
            <motion.div 
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="absolute top-14 right-4 bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg"
            >
              <PawPrint className="w-3 h-3" />
              {currentProfile.pets.length} {currentProfile.pets.length === 1 ? 'compagnon' : 'compagnons'}
            </motion.div>
          )}
        </div>

        {/* Infos */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {currentProfile.displayName}, {currentProfile.age}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Client depuis {currentProfile.memberSince || '2024'}
              </p>
              {currentProfile.crowns > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-yellow-500">👑</span>
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {currentProfile.crowns} couronnes
                  </span>
                </div>
              )}
            </div>
            
            {/* Score de compatibilité */}
            {currentProfile.soulCompatibility && (
              <div className="flex flex-col items-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-lg shadow-lg ${
                  currentProfile.soulCompatibility.level === 'twin_flame' 
                    ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white animate-pulse' 
                    : currentProfile.soulCompatibility.level === 'soulmate'
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                      : currentProfile.soulCompatibility.level === 'high'
                        ? 'bg-gradient-to-br from-violet-500 to-purple-500 text-white'
                        : 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white'
                }`}>
                  <span className="text-2xl">{currentProfile.soulCompatibility.emoji}</span>
                </div>
                <span className="text-lg font-bold mt-1 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  {currentProfile.soulCompatibility.percentage}%
                </span>
                <span className={`text-xs text-center ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                  {currentProfile.soulCompatibility.level === 'twin_flame' && '🔥 Âmes jumelles !'}
                  {currentProfile.soulCompatibility.level === 'soulmate' && '✨ Âmes sœurs !'}
                  {currentProfile.soulCompatibility.level === 'high' && '💜 Forte connexion'}
                  {currentProfile.soulCompatibility.level === 'medium' && '🌟 À découvrir'}
                  {currentProfile.soulCompatibility.level === 'exploring' && '🌱 À explorer'}
                </span>
                {currentProfile.soulCompatibility.isGlobalScore && (
                  <span className={`text-[10px] mt-1 px-2 py-0.5 rounded-full ${
                    isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                  }`}>
                    ✨ Score Global
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Bio */}
          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            "{currentProfile.bio || "Hey, je suis sur Heartbeats ! 👋"}"
          </p>

          {/* Passions communes */}
          {currentProfile.soulCompatibility?.common_passions?.length > 0 && (
            <div className="mb-4">
              <p className={`text-xs uppercase tracking-wide mb-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                ✨ Passions en commun
              </p>
              <div className="flex flex-wrap gap-2">
                {currentProfile.soulCompatibility.common_passions.slice(0, 5).map((passion, idx) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isDarkMode 
                        ? 'bg-purple-900/50 text-purple-300 border border-purple-700' 
                        : 'bg-purple-100 text-purple-700 border border-purple-200'
                    }`}
                  >
                    {passion}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* 🌙 Score Nyx 2.0 - Compatibilité d'ombre */}
          {currentProfile.nyxCompatibility?.hasNyx2 && (
            <div className={`mb-4 p-3 rounded-xl ${
              isDarkMode ? 'bg-indigo-950/50 border border-indigo-800' : 'bg-indigo-50 border border-indigo-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs uppercase tracking-wide flex items-center gap-1.5 ${
                  isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                }`}>
                  <Moon className="w-3 h-3" />
                  Compatibilité d'ombre
                </p>
                <span className={`text-lg font-bold ${
                  currentProfile.nyxCompatibility.level === 'intense' 
                    ? 'text-purple-400' 
                    : currentProfile.nyxCompatibility.level === 'high'
                      ? 'text-indigo-400'
                      : 'text-blue-400'
                }`}>
                  {currentProfile.nyxCompatibility.percentage}%
                </span>
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                {currentProfile.nyxCompatibility.chemistry}
              </p>
              {currentProfile.nyxCompatibility.insights?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {currentProfile.nyxCompatibility.insights.slice(0, 2).map((insight, idx) => (
                    <p key={idx} className={`text-[11px] flex items-start gap-1 ${
                      isDarkMode ? 'text-indigo-400/80' : 'text-indigo-600/80'
                    }`}>
                      <span>{insight.emoji}</span>
                      <span>{insight.text}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* 🐾 Animaux de compagnie */}
          {currentProfile.pets && currentProfile.pets.length > 0 && (
            <div className={`mb-4 p-3 rounded-xl ${
              isDarkMode ? 'bg-amber-950/30 border border-amber-800/50' : 'bg-amber-50 border border-amber-200'
            }`}>
              <p className={`text-xs uppercase tracking-wide mb-2 flex items-center gap-1.5 ${
                isDarkMode ? 'text-amber-400' : 'text-amber-600'
              }`}>
                <PawPrint className="w-3 h-3" />
                Ses compagnons
              </p>
              <div className="flex flex-wrap gap-2">
                {currentProfile.pets.map((pet, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                      isDarkMode 
                        ? 'bg-amber-900/50 text-amber-200' 
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    <span className="text-sm">
                      {pet.species === 'dog' && '🐕'}
                      {pet.species === 'cat' && '🐱'}
                      {pet.species === 'bird' && '🐦'}
                      {pet.species === 'rabbit' && '🐰'}
                      {pet.species === 'hamster' && '🐹'}
                      {pet.species === 'fish' && '🐠'}
                      {pet.species === 'reptile' && '🦎'}
                      {!['dog', 'cat', 'bird', 'rabbit', 'hamster', 'fish', 'reptile'].includes(pet.species) && '🐾'}
                    </span>
                    <span className="text-xs font-medium">{pet.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Boutons d'action */}
      <div className="flex justify-center items-center gap-4 mt-6">
        {/* Pass */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={discover.handlePass}
          className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
        >
          <X className="w-7 h-7" />
        </motion.button>

        {/* Super Like */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={discover.handleSuperLike}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg flex items-center justify-center text-white"
        >
          <Star className="w-6 h-6" />
        </motion.button>

        {/* Like */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={discover.handleLike}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 shadow-lg flex items-center justify-center text-white"
        >
          <Heart className="w-7 h-7" />
        </motion.button>

        {/* Cadeau */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onSendGift?.(currentProfile)}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg flex items-center justify-center text-white"
        >
          <Gift className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Match Animation */}
      <AnimatePresence>
        {discover.showMatchAnimation && discover.matchedProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
            onClick={discover.closeMatchAnimation}
          >
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              className="text-center p-8"
            >
              <div className="text-6xl mb-4">💜</div>
              <h2 className="text-3xl font-bold text-white mb-2">C'est un Match !</h2>
              <p className="text-gray-300 mb-6">
                Toi et {discover.matchedProfile.displayName} vous êtes likés mutuellement !
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => {
                    discover.closeMatchAnimation();
                    onOpenChat?.(discover.matchedProfile);
                  }}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Envoyer un message
                </Button>
                <Button
                  onClick={discover.closeMatchAnimation}
                  variant="outline"
                  className="text-white border-white hover:bg-white/10"
                >
                  Continuer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscoverTab;
